import { LabelButton } from '@teamsnap/snap-ui';
import * as React from 'react';

// Local imports
import RecordOfflinePaymentModal from 'components/registration/v2/RecordOfflinePaymentModal/RecordOfflinePaymentModal';
import SendEmailModal from 'components/registration/v2/SendEmailModal';
import { reactiveFeedbackMessage } from 'core/apollo-cache';
import ENVIRONMENT from 'core/environment';
import { FeatureFlagConfig, FeatureFlags } from 'core/feature-flags';
import { convertToPennies } from 'core/utils';
import { FeatureFlagService } from 'frontend-toolkit';

import {
  LoadingType,
  useOrderDetails,
  useOrderDetailsActions,
} from 'hooks/orderDetails';
import { sendEmailToCustomer } from 'hooks/orderDetails/service';
import { useShowOrganization } from '../../hooks/GraphQL';
import { AdjustOrderDrawer } from './adjust-order-drawer/adjust-order-drawer';

const OrderDetailsActions = () => {
  const [isEmailModalOpen, setIsEmailModalOpen] =
    React.useState<boolean>(false);
  const [isAdjustOrderOpen, setIsAdjustOrderOpen] =
    React.useState<boolean>(false);
  const [isRecordPaymentModalOpen, setIsRecordPaymentModalOpen] =
    React.useState<boolean>(false);

  const { orgId, order, loading } = useOrderDetails();
  const {
    createOfflinePayment,
    getNotes,
    getInstallments,
    getTransactions,
    getOrder,
  } = useOrderDetailsActions();

  const [orgData] = useShowOrganization(orgId);
  const org = orgData?.showOrganization;

  const offlinePaymentAllowed = FeatureFlagService.isFeatureEnabledForOrg(
    FeatureFlagConfig[FeatureFlags.OFFLINE_PAYMENTS],
    ENVIRONMENT,
    +orgId || undefined
  );

  const sendEmailRequest = async ({
    subjectEmail,
    messageEmail,
    sendFromEmail,
  }: {
    subjectEmail: string;
    messageEmail: string;
    sendFromEmail: string;
  }) => {
    try {
      const response = await sendEmailToCustomer<{ id: number }>(
        order?.id,
        sendFromEmail,
        subjectEmail,
        messageEmail
      );

      if (response?.id) {
        reactiveFeedbackMessage({
          message: 'Your email has been sent',
          isError: false,
          autoclose: true,
        });

        setIsEmailModalOpen(false);

        return true;
      } else {
        reactiveFeedbackMessage({
          message: 'Oops!  We dropped the ball. Please send your email again.',
          isError: true,
          autoclose: true,
        });
      }
    } catch (error) {
      if (error.graphQLErrors) {
        const [errors] = error.graphQLErrors;

        reactiveFeedbackMessage({
          message: errors.message,
          isError: true,
          autoclose: true,
        });
      } else {
        reactiveFeedbackMessage({
          message: 'Oops!  We dropped the ball. Please send your email again.',
          isError: true,
          autoclose: true,
        });
      }
    }
    return false;
  };

  const recordOfflinePayment = async (data) => {
    try {
      const response = await createOfflinePayment(
        order?.id,
        convertToPennies(data.amount),
        data.paymentType.toLocaleLowerCase(),
        data.note
      );

      if (response?.id) {
        reactiveFeedbackMessage({
          message: 'Your payment has been recorded',
          isError: false,
          autoclose: true,
        });

        getNotes(order?.id);
        getOrder(order?.orderNumber);
        getTransactions(order?.id);
        getInstallments(order?.id);
        setIsRecordPaymentModalOpen(false);
      } else {
        reactiveFeedbackMessage({
          message: 'Oops!  We dropped the ball. Please try again.',
          isError: true,
          autoclose: true,
        });
      }
    } catch (error) {
      if (error.graphQLErrors) {
        const [errors] = error.graphQLErrors;
        reactiveFeedbackMessage({
          message: errors.message,
          isError: true,
          autoclose: true,
        });
      } else {
        reactiveFeedbackMessage({
          message: 'Oops!  We dropped the ball. Please try again.',
          isError: true,
          autoclose: true,
        });
      }
    }
  };

  return (
    <div className="Grid-cell sui-flex sui-justify-end">
      <div className="sui-flex sui-gap-1 sui-justify-between">
        <LabelButton
          onClick={() => setIsAdjustOrderOpen(true)}
          variantType="secondary"
          data-testid="order-details-actions--adjust-order-button"
        >
          Adjust Order
        </LabelButton>

        {offlinePaymentAllowed ? (
          <LabelButton
            onClick={() => setIsRecordPaymentModalOpen(true)}
            data-testid="order-details-actions--record-payment-button"
            variantType="secondary"
            disabled={
              order?.amountRemaining <= 0 || order?.status === 'pending'
            }
          >
            Record Payment
          </LabelButton>
        ) : null}

        <LabelButton
          onClick={() => setIsEmailModalOpen(true)}
          data-testid="order-details-actions--email-button"
          variantType="secondary"
        >
          Email Registrant
        </LabelButton>
      </div>

      <SendEmailModal
        totalRegistrants={null}
        selectedRecordsLength={null}
        isSendingEmailToAllRegistrants={false}
        isSendingIndividualEmail={true}
        selectedIndividualEmail={order?.customerEmail}
        show={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        onSubmit={sendEmailRequest}
        fromEmail={null}
      />

      <RecordOfflinePaymentModal
        show={order?.amountRemaining && isRecordPaymentModalOpen}
        onClose={() => setIsRecordPaymentModalOpen(false)}
        isSubmitting={loading[LoadingType.CreateOfflinePayment]}
        onSubmit={(data) => recordOfflinePayment(data)}
        balanceDue={order?.amountRemaining}
        currency={org?.currency || 'USD'}
        currencySymbol={org?.currencySymbol || '$'}
      />

      <AdjustOrderDrawer
        isOpen={isAdjustOrderOpen}
        handleClose={() => setIsAdjustOrderOpen(false)}
      />
    </div>
  );
};

export default OrderDetailsActions;
